const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;

let _patrolId = null;
export const setPatrolId = (id) => { _patrolId = id; };
export const getPatrolId = () => _patrolId;

const ERROR_MESSAGES = {
    'patrol/missing-patrol-id':       'Unable to identify your patrol. Please log out and log back in.',
    'patrol/not-a-member':            'You do not have access to this patrol. Contact your patrol director if you believe this is an error.',
    'patrol/account-disabled':        'Your account has been disabled. Contact your patrol director to restore access.',
    'patrol/membership-check-failed': 'We were unable to verify your membership. Please try again later.',
    'core/unauthorized':              'Your session has expired or is invalid. Please log out and log back in to continue.',
    'auth/authentication-required':   'You must be logged in to perform this action.',
    'auth/access-denied':             'You do not have permission to perform this action.',
    'auth/authorization-failed':      'An error occurred while verifying your permissions. Please try again later.',
    'core/invalid-request':           'The request was invalid. Please check your input and try again.',
    'schedule/create-event-failure':  'Unable to save the event. Please check the dates and times and try again.',
    'schedule/update-event-failure':  'Unable to save the event. Please check the dates and times and try again.',
    'schedule/remove-event-failure':  'Unable to delete the event. Please try again.',
    'Event registration times must be valid dates and times.': 'Event registration times must be valid dates and times.',
};

const friendlyError = (apiError, fallback) => ERROR_MESSAGES[apiError] || fallback || 'Something went wrong. Please try again later.';

// MARK: getUserProfile (core — no patrolAuth needed)
export const getUserProfile = async (uid, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/core/users/${ uid }`, payload);

        if (response.ok) {
            return await response.json();
        } else {
            return null;
        }
    } catch (error) {
        console.error('getUserProfile:', error);
        return null;
    }
};

// MARK: getCertifications
export const getCertifications = async (token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/certify/certifications`, payload);

        if (response.ok) {
            const json = await response.json();

            return Array.isArray(json)
                ? json.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                : [];
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to load certifications. Please try again.') };
        }
    } catch (error) {
        console.error('getCertifications:', error);

        return { error: friendlyError(null, 'Unable to load certifications. Please try again.') };
    }
};

// MARK: getCertification
export const getCertification = async (id, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/certifications/${ id }`, payload);

        if (response.ok) {
            return await response.json();
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to load certification. Please try again.') };
        }
    } catch (error) {
        console.error('getCertification:', error);

        return { error: friendlyError(null, 'Unable to load certification. Please try again.') };
    }
};

// MARK: getEvents
export const getEvents = async (token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/schedule/events`, payload);

        if (response.ok) {
            const json = await response.json();

            return Array.isArray(json)
                ? json.filter(event => !event.canceled).sort((a, b) => a.start > b.start)
                : [];
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to load events. Please try again.') };
        }
    } catch (error) {
        console.error('getEvents:', error);

        return { error: friendlyError(null, 'Unable to load events. Please try again.') };
    }
};

// MARK: addEvent
export const addEvent = async (data, token) => {
    try {
        const payload = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            },
            body: JSON.stringify(data)
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/schedule/events`, payload);

        if (response.ok) {
            return true;
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to save event. Please try again.') };
        }
    } catch (error) {
        console.error('addEvent:', error);

        return { error: friendlyError(null, 'Unable to save event. Please try again.') };
    }
};

// MARK: updateEvent
export const updateEvent = async (data, token) => {
    try {
        const payload = {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            },
            body: JSON.stringify(data)
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/schedule/events`, payload);

        if (response.ok) {
            return true;
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to update event. Please try again.') };
        }
    } catch (error) {
        console.error('updateEvent:', error);

        return { error: friendlyError(null, 'Unable to update event. Please try again.') };
    }
};

// MARK: deleteEvent
export const deleteEvent = async (id, token) => {
    try {
        const payload = {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/schedule/events/${ id }`, payload);

        if (response.ok) {
            return true;
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to delete event. Please try again.') };
        }
    } catch (error) {
        console.error('deleteEvent:', error);

        return { error: friendlyError(null, 'Unable to delete event. Please try again.') };
    }
};

// MARK: getDuties
export const getDuties = async (id, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/duties/${ id }`, payload);

        if (response.ok) {
            const json = await response.json();

            return Array.isArray(json)
                ? json.sort((a, b) => a.shift.start - b.shift.start)
                : [];
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to load duty assignments. Please try again.') };
        }
    } catch (error) {
        console.error('getDuties:', error);

        return { error: friendlyError(null, 'Unable to load duty assignments. Please try again.') };
    }
};

// MARK: createSeries
export const createSeries = async (shift, token) => {
    try {
        const payload = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            },
            body: JSON.stringify(shift)
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/schedule/series`, payload);
        
        if (response.ok) {
            const location = response.headers.get('Location');

            return location.split('/').pop();
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error || json?.message, 'Unable to create series. Please try again.') };
        }
    } catch (error) {
        console.error('createSeries:', error);

        return { error: friendlyError(null, 'Unable to create series. Please try again.') };
    }
};

// MARK: getShift
export const getShift = async (id, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/schedule/shifts/${ id }`, payload);
        
        if (response.ok) {
            return await response.json();
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to load shift. Please try again.') };
        }
    } catch (error) {
        console.error('getShift:', error);

        return { error: friendlyError(null, 'Unable to load shift. Please try again.') };
    }
};

// MARK: getShiftsBySeries
export const getShiftsBySeries = async (id, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/schedule/shifts/series/${ id }`, payload);

        if (response.ok) {
            const json = await response.json();

            return Array.isArray(json)
                ? json.sort((a, b) => {
                    if (a.date !== b.date) {
                        return Date.parse(a.date) - Date.parse(b.date); // sort by date ASC
                    }

                    return (a.name || '').localeCompare(b.name || ''); // if dates are equal, sort by name ASC
                })
                : [];
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to load series shifts. Please try again.') };
        }
    } catch (error) {
        console.error('getShiftsBySeries:', error);

        return { error: friendlyError(null, 'Unable to load series shifts. Please try again.') };
    }
};

// MARK: createShift
export const createShift = async (data, token) => {
    try {
        const payload = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            },
            body: JSON.stringify(data)
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/schedule/shifts`, payload);

        if (response.ok) {
            const location = response.headers.get('Location');

            return location.split('/').pop();
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to create shift. Please try again.') };
        }
    } catch (error) {
        console.error('createShift:', error);

        return { error: friendlyError(null, 'Unable to create shift. Please try again.') };
    }
};

// MARK: updateShift
export const updateShift = async (data, token) => {
    try {
        const payload = {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            },
            body: JSON.stringify(data)
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/schedule/shifts`, payload);

        if (response.ok) {
            return true;
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to update shift. Please try again.') };
        }
    } catch (error) {
        console.error('updateShift:', error);

        return { error: friendlyError(null, 'Unable to update shift. Please try again.') };
    }
};

// MARK: removeShift
export const removeShift = async (id, token) => {
    try {
        const payload = {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            }
        };

        const url = `${ apiEndpoint }/api/v1/schedule/shifts/${ id }`;
        const response = await fetch(url, payload);

        if (response.ok) {
            return true;
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to remove shift. Please try again.') };
        }
    } catch (error) {
        console.error('removeShift:', error);

        return { error: friendlyError(null, 'Unable to remove shift. Please try again.') };
    }
};

// MARK: cancelShift
export const cancelShift = async (id, token, notify = false) => {
    try {
        const payload = {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            }
        };

        const url = `${ apiEndpoint }/api/v1/schedule/shifts/${ id }${ notify ? '?notify=true' : '' }`;
        const response = await fetch(url, payload);

        if (response.ok) {
            return true;
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to cancel shift. Please try again.') };
        }
    } catch (error) {
        console.error('cancelShift:', error);

        return { error: friendlyError(null, 'Unable to cancel shift. Please try again.') };
    }
};

// MARK: getRole
export const getRole = async (id, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/schedule/roles/${ id }`, payload);

        if (response.ok) {
            return await response.json();
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to load role. Please try again.') };
        }
    } catch (error) {
        console.error('getRole:', error);

        return { error: friendlyError(null, 'Unable to load role. Please try again.') };
    }
};

// MARK: getAllRoles
export const getAllRoles = async (token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/schedule/roles`, payload);

        if (response.ok) {
            const json = await response.json();

            return Array.isArray(json)
                ? json.sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''))
                : [];
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to load roles. Please try again.') };
        }
    } catch (error) {
        console.error('getRoles:', error);

        return { error: friendlyError(null, 'Unable to load roles. Please try again.') };
    }
};

// MARK: createRole
export const createRole = async (role, token) => {
    try {
        const payload = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            },
            body: JSON.stringify(role)
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/schedule/roles`, payload);

        if (response.ok) {
            const location = response.headers.get('Location');

            return location.split('/').pop();
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to create role. Please try again.') };
        }
    } catch (error) {
        console.error('createRole:', error);

        return { error: friendlyError(null, 'Unable to create role. Please try again.') };
    }
};

// MARK: updateRole
export const updateRole = async (role, token) => {
    try {
        const payload = {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            },
            body: JSON.stringify(role)
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/schedule/roles`, payload);

        if (response.ok) {
            return true;
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to update role. Please try again.') };
        }
    } catch (error) {
        console.error('updateRole:', error);

        return { error: friendlyError(null, 'Unable to update role. Please try again.') };
    }
};

// MARK: removeRole
export const removeRole = async (id, token) => {
    try {
        const payload = {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/schedule/roles/${ id }`, payload);

        if (response.ok) {
            return true;
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to remove role. Please try again.') };
        }
    } catch (error) {
        console.error('removeRole:', error);

        return { error: friendlyError(null, 'Unable to remove role. Please try again.') };
    }
};

// MARK: getSlots
export const getSlots = async (token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/schedule/slots`, payload);

        if (response.ok) {
            const json = await response.json();

            return Array.isArray(json) ? json : [];
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to load slots. Please try again.') };
        }
    } catch (error) {
        console.error('getSlots:', error);

        return { error: friendlyError(null, 'Unable to load slots. Please try again.') };
    }
};

// MARK: updateSlots
export const updateSlots = async (slots, token) => {
    try {
        const payload = {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            },
            body: JSON.stringify(slots)
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/schedule/slots`, payload);

        if (response.ok) {
            return await response.json();
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to update slots. Please try again.') };
        }
    } catch (error) {
        console.error('updateSlots:', error);

        return { error: friendlyError(null, 'Unable to update slots. Please try again.') };
    }
};


// MARK: getPatroller
export const getPatroller = async (id, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/patrollers/${ id }`, payload);

        if (response.ok) {
            return await response.json();
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to load patroller. Please try again.') };
        }
    } catch (error) {
        console.error('getPatroller:', error);

        return { error: friendlyError(null, 'Unable to load patroller. Please try again.') };
    }
};

// MARK: getPatrollers
export const getPatrollers = async (token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/patrollers`, payload);

        if (response.ok) {
            const json = await response.json();

            return Array.isArray(json)
                ? json.sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''))
                : [];
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to load patrollers. Please try again.') };
        }
    } catch (error) {
        console.error('getPatrollers:', error);

        return { error: friendlyError(null, 'Unable to load patrollers. Please try again.') };
    }
};

// MARK: getShifts (manage)
export const getShifts = async (token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/shifts`, payload);

        if (response.ok) {
            const json = await response.json();

            return Array.isArray(json) ? json : [];
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to load shifts. Please try again.') };
        }
    } catch (error) {
        console.error('getShifts:', error);

        return { error: friendlyError(null, 'Unable to load shifts. Please try again.') };
    }
};

// MARK: getShiftsByEvent
export const getShiftsByEvent = async (eventId, token) => {
    try {
        const payload = {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/shifts/event/${ eventId }`, payload);

        if (response.ok) {
            const json = await response.json();

            return Array.isArray(json)
                ? json.sort((a, b) => {
                    if (a.date !== b.date) {
                        return Date.parse(a.date) - Date.parse(b.date);
                    }
                    return (a.name || '').localeCompare(b.name || '');
                })
                : [];
        } else {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to load shifts. Please try again.') };
        }
    } catch (error) {
        console.error('getShiftsByEvent:', error);

        return { error: friendlyError(null, 'Unable to load shifts. Please try again.') };
    }
};

// MARK: clearDutyAssignment
export const clearDutyAssignment = async (pid, sid, token) => {
    try {
        const payload = {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/patrollers/${ pid }/shift/${ sid }`, payload);

        if (!response.ok) {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to clear duty assignment. Please try again.') };
        }

        return true;
    } catch (error) {
        console.error('clearDutyAssignment:', error);

        return { error: friendlyError(null, 'Unable to clear duty assignment. Please try again.') };
    }
};

// MARK: setDutyAssignment
export const setDutyAssignment = async (pid, sid, rid, token) => {
    try {
        const payload = {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'Content-Type': 'application/json',
                'x-patrol-id': _patrolId
            }
        };

        const response = await fetch(`${ apiEndpoint }/api/v1/manage/patrollers/${ pid }/shift/${ sid }/role/${ rid }`, payload);

        if (!response.ok) {
            const json = await response.json().catch(() => null);

            return { error: friendlyError(json?.error, 'Unable to set duty assignment. Please try again.') };
        }

        return true;
    } catch (error) {
        console.error('setDutyAssignment:', error);

        return { error: friendlyError(null, 'Unable to set duty assignment. Please try again.') };
    }
};

// MARK: submitGitHubIssue
export const callSubmitGitHubIssue = async (email, type, desc, token) => {
    try {
        const payload = {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${ token }`,
                'x-patrol-id': _patrolId,
                "Content-Type": "application/json"
            },
            body: JSON.stringify(
                {
                    title: `${ email } - ${ type }`,
                    body: desc,
                    labels:["core"]
                }
            )
        };

        const response = await fetch(`${ apiEndpoint }/api/submit-github-issue`, payload);

        if (response.ok) {
            return true;
        } else {
            return { error: 'Unable to submit feedback. Please try again later.' };
        }
    } catch (error) {
        console.error('callSubmitGitHubIssue:', error);

        return { error: 'Unable to submit feedback. Please try again later.' };
    }
};

// MARK: event early access (admin only)
const earlyAccessHeaders = (token, json = false) => ({
    'Authorization': `Bearer ${ token }`,
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    'x-patrol-id': _patrolId
});

const earlyAccessFailure = async (response, fallback) => {
    const json = await response.json().catch(() => null);

    return {
        error: friendlyError(json?.error, json?.message || fallback),
        ...(response.status === 403 ? { forbidden: true } : {}),
        ...(Array.isArray(json?.userIds) ? { userIds: json.userIds } : {})
    };
};

export const getEventEarlyAccess = async (eventId, token) => {
    const fallback = 'Unable to load early access. Please try again.';

    try {
        const response = await fetch(`${ apiEndpoint }/api/v1/schedule/events/${ encodeURIComponent(eventId) }/early-access`, {
            method: 'GET',
            headers: earlyAccessHeaders(token)
        });

        return response.ok ? await response.json() : await earlyAccessFailure(response, fallback);
    } catch (error) {
        console.error('getEventEarlyAccess:', error);

        return { error: fallback };
    }
};

export const addEventEarlyAccess = async (eventId, kind, userIds, token) => {
    const fallback = 'Unable to add patrollers. Please try again.';

    try {
        const response = await fetch(`${ apiEndpoint }/api/v1/schedule/events/${ encodeURIComponent(eventId) }/early-access`, {
            method: 'POST',
            headers: earlyAccessHeaders(token, true),
            body: JSON.stringify({ kind, userIds })
        });

        return response.ok ? await response.json() : await earlyAccessFailure(response, fallback);
    } catch (error) {
        console.error('addEventEarlyAccess:', error);

        return { error: fallback };
    }
};

export const removeEventEarlyAccess = async (eventId, kind, userId, token) => {
    const fallback = 'Unable to remove patroller. Please try again.';

    try {
        const response = await fetch(`${ apiEndpoint }/api/v1/schedule/events/${ encodeURIComponent(eventId) }/early-access/${ encodeURIComponent(kind) }/${ encodeURIComponent(userId) }`, {
            method: 'DELETE',
            headers: earlyAccessHeaders(token)
        });

        return response.ok ? true : await earlyAccessFailure(response, fallback);
    } catch (error) {
        console.error('removeEventEarlyAccess:', error);

        return { error: fallback };
    }
};
